import { type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Badge, Col, Form, Row, Spinner } from 'react-bootstrap'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { type FormTask, type RedtailVariant } from '../AddTaskToCRMModal'
import { Button } from '../../../../../components/Button/Button'
import { type ResolvedIdentity, type Task } from '../../../../../models/MeetingSupport'
import { type CreateTaskPayload, type Identity } from '../../../../../store/meetingSupport/meetingSupport'
import { addToast } from '../../../../../store/toast/toastSlice'
import { TaskSourceType, useTaskMutations } from '../../../../Tasks/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { useMergeTasks, useTemplateTaskMerge } from './hooks'
import { type ActivityTemplate, REDTAIL_ACTIVITY_TYPE_OPTIONS, REDTAIL_IMPORTANCE_MAP, reactSelectBaseStyles } from './utils'
import { IntegrationServiceId } from '../../../../../common/integrations'
import Select from 'react-select'
import { type LexicalEditor } from 'lexical'
import { WritableLexicalEditor } from '../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { RichTextToolbarPlugin } from '../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { getEditorHtmlContent } from '../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'

export type EditMode = 'add' | 'merge' | 'edit' | 'template-merge'

export interface TaskContextDetails {
  roomId?: string
  sessionId?: string
  emailId?: string
  threadId?: string
  emailConnector?: string
  userId?: string
  contactId?: string
}

interface TaskEditSectionProps {
  mode: EditMode
  onEditSaved: () => void
  onEditCancelled: () => void
  taskToEdit?: Task | null
  initialTemplateId?: number | null
  templateForMerge?: ActivityTemplate | null
  existingVariant?: RedtailVariant | null
  onVariantSave?: (taskId: string, variant: RedtailVariant) => void
  tasksToMerge: FormTask[]
  assignedToOptions: SelectOption[]
  taskCategoryOptions: SelectOption[]
  activityTemplateOptions: SelectOption[]
  activityTemplateMap: Map<number, ActivityTemplate>
  serviceId: string
  type: TaskSourceType
  taskContextDetails: TaskContextDetails
}

export type EditTaskFormValues = {
  newTaskName: string
  newTaskDescription?: string
}

const _templateSelectStyles = {
  ...reactSelectBaseStyles,
}

export const TaskEditSection: React.FC<TaskEditSectionProps> = ({
  mode,
  onEditSaved,
  onEditCancelled,
  taskToEdit,
  initialTemplateId = null,
  templateForMerge = null,
  existingVariant = null,
  onVariantSave,
  tasksToMerge,
  assignedToOptions,
  taskCategoryOptions,
  activityTemplateOptions,
  activityTemplateMap,
  serviceId,
  type,
  taskContextDetails,
}) => {
  const { tasks, createTasks, isCreatingTasks, updateTaskById, isUpdatingTask, deleteTask, isDeletingTask } = useTaskMutations()
  const isSaving = isCreatingTasks || isUpdatingTask || isDeletingTask
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const dispatch = useAppDispatch()
  const taskMerger = useMergeTasks()
  const templateTaskMerger = useTemplateTaskMerge()
  const [isMerging, setIsMerging] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(initialTemplateId)
  const [mergedHtml, setMergedHtml] = useState<string | null>(existingVariant?.description ?? null)
  const editorRef = useRef<LexicalEditor>(null)

  const isRedtail = serviceId === IntegrationServiceId.REDTAIL
  const isTemplateMerge = mode === 'template-merge'
  const showTemplateDropdown = isRedtail && mode !== 'merge' && mode !== 'template-merge'
  const selectedTemplate = selectedTemplateId ? (activityTemplateMap.get(selectedTemplateId) ?? null) : null

  const defaultValues: EditTaskFormValues = {
    newTaskName: taskToEdit ? taskToEdit.task : '',
    newTaskDescription: taskToEdit ? taskToEdit.description : '',
  }
  const { control, handleSubmit, setValue } = useForm<EditTaskFormValues>({ defaultValues })

  const handleTemplateSelected = useCallback((templateId: number | null) => {
    setSelectedTemplateId(templateId)
  }, [])

  const templateBadges = useMemo(() => {
    if (!selectedTemplate) {
      return []
    }

    const badges: string[] = []

    if (selectedTemplate.category) {
      badges.push(`Category: ${selectedTemplate.category}`)
    } else if (selectedTemplate.categoryId) {
      const categoryLabel = taskCategoryOptions.find((o) => o.id === String(selectedTemplate.categoryId))?.label
      if (categoryLabel) {
        badges.push(`Category: ${categoryLabel}`)
      }
    }

    if (selectedTemplate.importance) {
      const importanceLabel = REDTAIL_IMPORTANCE_MAP[selectedTemplate.importance]
      if (importanceLabel) {
        badges.push(`Importance: ${importanceLabel}`)
      }
    }

    if (selectedTemplate.typeId) {
      const activityTypeLabel = REDTAIL_ACTIVITY_TYPE_OPTIONS.find((o) => o.id === String(selectedTemplate.typeId))?.label
      if (activityTypeLabel) {
        badges.push(`Activity Type: ${activityTypeLabel}`)
      }
    }

    return badges
  }, [selectedTemplate, taskCategoryOptions])

  useEffect(() => {
    if (mode !== 'merge' || tasksToMerge.length === 0) {
      return
    }
    const mergeTasks = async () => {
      setIsMerging(true)
      try {
        const result = await taskMerger(tasksToMerge)
        if (result) {
          setValue('newTaskName', result.subject)
          setValue('newTaskDescription', result.description)
        }
      } catch (error) {
        console.error('Failed to merge tasks:', error)
        dispatch(
          addToast({
            variant: 'error',
            messages: ['Failed to merge tasks. Please try again.'],
          }),
        )
      } finally {
        setIsMerging(false)
      }
    }

    mergeTasks()
  }, [mode, tasksToMerge, taskMerger, setValue, dispatch])

  useEffect(() => {
    if (mode !== 'template-merge' || !taskToEdit) {
      return
    }

    if (existingVariant) {
      setValue('newTaskName', existingVariant.taskName)
      setMergedHtml(existingVariant.description)
      return
    }

    if (!templateForMerge) {
      return
    }

    const runTemplateMerge = async () => {
      setIsMerging(true)
      try {
        const result = await templateTaskMerger(templateForMerge, {
          subject: taskToEdit.task || '',
          description: taskToEdit.description || '',
        })
        if (result) {
          setValue('newTaskName', result.subject)
          setMergedHtml(result.description)
        }
      } catch (error) {
        console.error('Failed to merge with template:', error)
        dispatch(
          addToast({
            variant: 'error',
            messages: ['Failed to merge task with template. Please try again.'],
          }),
        )
      } finally {
        setIsMerging(false)
      }
    }

    runTemplateMerge()
  }, [mode, templateForMerge, taskToEdit, existingVariant, templateTaskMerger, setValue, dispatch])

  const getAssigneeName = (id: string | string[] | null) => {
    if (Array.isArray(id)) {
      return id?.length
        ? assignedToOptions
            .filter((o) => id.includes(o.id))
            .map((o) => o.label)
            .join(', ')
        : 'N/A'
    } else if (typeof id === 'string') {
      return assignedToOptions.find((o) => o.id === id)?.label || 'N/A'
    } else {
      return 'N/A'
    }
  }
  const getCategoryName = (id: string | null) => (id ? taskCategoryOptions.find((o) => o.id === id)?.label || 'N/A' : 'N/A')

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Edit Task'
      case 'merge':
        return 'New Task'
      case 'add':
        return 'Add New Task'
      case 'template-merge':
        return 'Edit Task with Template'
      default:
        return ''
    }
  }

  const buildCreateTaskPayload = (taskName: string, taskDescription: string, assignedTo: ResolvedIdentity): CreateTaskPayload => ({
    task: taskName,
    description: taskDescription,
    assignedTo: { id: assignedTo.id, type: assignedTo.type },
    type: type,
    roomId: taskContextDetails.roomId,
    sessionId: taskContextDetails.sessionId,
    emailId: taskContextDetails.emailId,
    threadId: taskContextDetails.threadId,
    emailConnector: taskContextDetails.emailConnector,
  })
  const { tracked } = useTracking()

  const _handleSaveCreateTask = async (formValues: EditTaskFormValues) => {
    const newTaskAssignedTo: Identity = { type: 'user', id: authenticatedUser.id }
    const payload = buildCreateTaskPayload(formValues.newTaskName || 'New Task', formValues.newTaskDescription || '', newTaskAssignedTo)
    await createTasks([payload])
  }

  const handleSaveCreateTask = tracked(_handleSaveCreateTask, {
    eventName: TrackEvent.TASK_CREATED,
  })

  const _handleSaveMergedTasks = async (formValues: EditTaskFormValues) => {
    const deletePromises = tasks.filter((t) => tasksToMerge.some((tt) => tt.id === t.id)).map((task) => deleteTask(task))

    await Promise.all(deletePromises)
    const newTaskAssignedTo: Identity = { type: 'user', id: authenticatedUser.id }
    const payload = buildCreateTaskPayload(formValues.newTaskName || 'New Merged Task', formValues.newTaskDescription || '', newTaskAssignedTo)
    await createTasks([payload])
  }

  const handleSaveMergedTasks = tracked(_handleSaveMergedTasks, {
    eventName: TrackEvent.TASKS_MERGED,
    eventDetails: { tasks: tasksToMerge.map((t) => t.id) },
  })

  const _handleSaveEditTask = async (formValues: EditTaskFormValues) => {
    const existingTask = tasks.find((t) => t.id === taskToEdit?.id)
    if (!existingTask) {
      return
    }
    await updateTaskById(existingTask.id, { task: formValues.newTaskName, description: formValues.newTaskDescription })
  }

  const handleSaveEditTask = tracked(_handleSaveEditTask, {
    eventName: TrackEvent.TASK_UPDATED,
    eventDetails: { task: taskToEdit?.id, type: 'edit' },
  })

  const handleOnSave = async (formValues: EditTaskFormValues) => {
    try {
      if (mode === 'add') {
        await handleSaveCreateTask(formValues)
      } else if (mode === 'merge') {
        await handleSaveMergedTasks(formValues)
      } else if (mode === 'edit') {
        await handleSaveEditTask(formValues)
      } else if (mode === 'template-merge') {
        const htmlDescription = editorRef.current ? getEditorHtmlContent(editorRef.current.getEditorState()) : formValues.newTaskDescription || ''
        if (onVariantSave && taskToEdit) {
          onVariantSave(taskToEdit.id, { taskName: formValues.newTaskName, description: htmlDescription ?? '' })
        }
      }

      dispatch(
        addToast({
          variant: 'success',
          title: 'Success',
          messages: [`Task successfully ${mode === 'merge' ? 'merged' : mode === 'template-merge' ? 'updated' : mode + 'ed'}.`],
        }),
      )
    } catch (error) {
      console.error(`Failed to ${mode} task:`, error)
      dispatch(
        addToast({
          variant: 'error',
          messages: [`Failed to ${mode === 'template-merge' ? 'update' : mode} task. Please try again.`],
        }),
      )
    }
    onEditSaved()
  }

  return (
    <Row>
      <Col className={'px-4 py-3'}>
        {mode === 'merge' && (
          <div className={'d-flex flex-column align-items-center pb-3'}>
            <div className='w-100 px-3 pt-3 mb-3 border rounded zocks-border-color-gray-300 align-items-center gap-3'>
              <h5>Tasks to merge</h5>
              <ul className='list-unstyled'>
                {tasksToMerge.map((task) => (
                  <li key={task.id} className='mt-3'>
                    <p className='mb-1'>
                      <strong>{task.task}:</strong> {task.description || 'No description.'}
                    </p>
                    <small className='text-muted d-flex gap-1'>
                      <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{getAssigneeName(task.multipleAssignedTo.length ? task.multipleAssignedTo : (task.assignedTo ?? null))}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{task.priority}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{getCategoryName(task.category)}</span>
                    </small>
                  </li>
                ))}
              </ul>
            </div>
            <i className={'bi bi-arrow-down fs-5'} />
          </div>
        )}

        {isTemplateMerge && taskToEdit && (
          <div className='d-flex flex-column align-items-center pb-3'>
            <div className='w-100 px-3 pt-3 pb-3 mb-3 border rounded zocks-border-color-gray-300'>
              <h6 className='text-uppercase fw-bold text-muted mb-2'>Zocks Task</h6>
              <p className='mb-1'>
                <strong>Subject:</strong> {taskToEdit.task || 'N/A'}
              </p>
              <p className='mb-0'>
                <strong>Description:</strong> {taskToEdit.description || 'N/A'}
              </p>
            </div>
            <i className='bi bi-arrow-down fs-5' />
          </div>
        )}

        <div className='zocks-bg-color-gray-100 p-3 border rounded zocks-border-color-gray-300'>
          <h5 className='mb-3'>{getTitle()}</h5>

          {showTemplateDropdown && (
            <Form.Group controlId='activityTemplate' className='mb-3'>
              <Form.Label className={'text-uppercase bold'}>Activity template (optional)</Form.Label>
              <Select<SelectOption, false>
                isSearchable
                isDisabled={mode === 'edit'}
                options={activityTemplateOptions}
                value={selectedTemplateId ? (activityTemplateOptions.find((o) => o.id === String(selectedTemplateId)) ?? null) : null}
                onChange={(option) => handleTemplateSelected(option?.id ? Number(option.id) : null)}
                menuPortalTarget={document.body}
                menuPlacement='auto'
                placeholder='Select a template…'
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.label}
                hideSelectedOptions={false}
                styles={_templateSelectStyles}
                isClearable={false}
              />
            </Form.Group>
          )}

          <Form.Group controlId='newTaskName'>
            <div className='d-flex align-items-center justify-content-between'>
              <Form.Label className={'text-uppercase bold mb-0'}>Task name</Form.Label>
            </div>
            <Controller
              name='newTaskName'
              control={control}
              rules={{ required: 'Task name is required' }}
              render={({ field, fieldState }) => (
                <>
                  <div className='position-relative'>
                    <Form.Control type='text' placeholder='Enter task name' {...field} disabled={isSaving || isMerging} />
                    {isMerging && (
                      <div className='position-absolute top-50 end-0 translate-middle-y pe-2'>
                        <Spinner animation='border' size='sm' />
                      </div>
                    )}
                  </div>
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Form.Group>
          <Form.Group controlId='newTaskDescription' className='mt-3'>
            <div className='d-flex align-items-center justify-content-between'>
              <Form.Label className={'text-uppercase bold mb-0'}>Task description</Form.Label>
            </div>
            {isTemplateMerge ? (
              <div className='position-relative'>
                {isMerging ? (
                  <div className='d-flex align-items-center justify-content-center p-4 border rounded'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    <span className='text-muted'>Applying your template...</span>
                  </div>
                ) : mergedHtml ? (
                  <WritableLexicalEditor
                    key='merged'
                    ref={editorRef}
                    initialHtmlValue={mergedHtml}
                    lexicalNodes={LEXICAL_NODES}
                    header={<RichTextToolbarPlugin />}
                    editorContainerClassName='task-description-editor-content'
                  />
                ) : (
                  <WritableLexicalEditor
                    key='plain'
                    ref={editorRef}
                    initialTextValue={taskToEdit?.description || ''}
                    lexicalNodes={LEXICAL_NODES}
                    header={<RichTextToolbarPlugin />}
                    editorContainerClassName='task-description-editor-content'
                  />
                )}
              </div>
            ) : (
              <Controller
                name='newTaskDescription'
                control={control}
                render={({ field }) => (
                  <div className='position-relative'>
                    <Form.Control as='textarea' rows={3} placeholder='Enter a brief description if needed' {...field} disabled={isSaving || isMerging} />
                    {isMerging && (
                      <div className='position-absolute top-0 end-0 mt-2 me-2'>
                        <Spinner animation='border' size='sm' />
                      </div>
                    )}
                  </div>
                )}
              />
            )}
          </Form.Group>

          {selectedTemplate && templateBadges.length > 0 && (
            <div className='mt-3'>
              <Form.Label className='text-uppercase bold'>Fields mapped from template</Form.Label>
              <div className='d-flex flex-wrap gap-2'>
                {templateBadges.map((badge) => (
                  <Badge key={badge} bg='light' text='dark' className='border px-2 py-1 fw-normal'>
                    {badge}
                  </Badge>
                ))}
              </div>
              <small className='text-muted d-block mt-1'>
                These parameters will be applied to the new task. You can edit them in the task table after saving.
              </small>
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end mt-3 gap-2'>
          <Button variant='secondary' onClick={onEditCancelled} disabled={isSaving || isMerging}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmit(handleOnSave)} disabled={isSaving || isMerging} loading={isSaving}>
            {mode === 'merge' ? 'Merge Tasks' : 'Save'}
          </Button>
        </div>
      </Col>
    </Row>
  )
}
